import { ChangeEvent, ForwardedRef, MutableRefObject, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

const getHiddenValueSetter = () => {
    if (typeof window === 'undefined') return undefined;
    return Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
};

type UseHiddenInputAdapterParams = {
    forwardedRef: ForwardedRef<HTMLInputElement>;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

type UseHiddenInputAdapterResult = {
    hiddenInputRef: MutableRefObject<HTMLInputElement | null>;
    setHiddenInputValue: (next: string) => void;
    handleHiddenChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const useHiddenInputAdapter = ({ forwardedRef, onChange }: UseHiddenInputAdapterParams): UseHiddenInputAdapterResult => {
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => hiddenInputRef.current as HTMLInputElement, [hiddenInputRef]);

    const hiddenValueSetter = useMemo(() => getHiddenValueSetter(), []);

    const setHiddenInputValue = useCallback(
        (next: string) => {
            const element = hiddenInputRef.current;
            if (!element) return;
            if (hiddenValueSetter) {
                hiddenValueSetter.call(element, next);
            } else {
                element.value = next;
            }
            element.dispatchEvent(new Event('input', { bubbles: true }));
        },
        [hiddenValueSetter]
    );

    const handleHiddenChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            onChange?.(event);
        },
        [onChange]
    );

    return { hiddenInputRef, setHiddenInputValue, handleHiddenChange };
};

export type { UseHiddenInputAdapterParams, UseHiddenInputAdapterResult };
export { useHiddenInputAdapter };
